<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[15000] flex items-center justify-center bg-[#090E11]/45 backdrop-blur-md max-lg:items-end max-lg:p-0"
      @click.self="closePopup"
    >
      <transition name="login-required-modal" appear>
        <div
          class="relative box-border w-[calc(100vw-48px)] max-w-[440px] rounded-[24px] p-10 px-8 pb-8 flex flex-col items-center text-center max-lg:w-full max-lg:rounded-t-[24px] max-lg:rounded-b-none max-lg:px-6 max-lg:pb-6 max-lg:pt-10 max-lg:animate-slideUp"
          :class="isWasabi
            ? 'bg-[#070D05] border border-[#75B13B] shadow-[0_-20px_60px_rgba(117,177,59,0.2)]'
            : 'bg-white border border-[#EEF0F2] shadow-[0_10px_40px_rgba(0,0,0,0.1)]'"
        >
          <button
            type="button"
            class="absolute top-4 right-4 w-9 h-9 border-0 rounded-full bg-transparent inline-flex items-center justify-center cursor-pointer transition-all duration-200 active:scale-95 hover:scale-105 hover:opacity-80 max-lg:top-3 max-lg:right-3"
            aria-label="Close"
            @click="closePopup"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 18L12 12M12 12L6 6M12 12L18 6M12 12L6 18" :stroke="isWasabi ? '#ffffff' : '#071534'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          
          <div class="w-full flex flex-col items-center">
            <div class="mb-5 flex items-center justify-center">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15V17M12 7V13M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" :stroke="isWasabi ? '#75B13B' : '#E1251B'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <h2
              class="m-0 mb-3 font-sans text-[22px] font-bold leading-7"
              :class="isWasabi ? 'text-white' : 'text-[#0C1D37]'"
            >
              {{ $t('LoginRequired.title') }}
            </h2>
            <p
              class="m-0 mb-8 font-sans text-sm font-medium leading-[22px] max-w-[320px]"
              :class="isWasabi ? 'text-[#DBE1F0]' : 'text-[#505C78]'"
            >
              {{ $t('LoginRequired.message') }}
            </p>
            
            <div class="w-full flex flex-col gap-3">
              <button
                class="w-full h-12 rounded-full font-sans text-base font-bold cursor-pointer inline-flex items-center justify-center transition-all duration-200 active:scale-[0.98]"
                :class="isWasabi
                  ? 'bg-transparent text-white border border-[#75B13B] hover:bg-[#75B13B] hover:text-black'
                  : 'bg-[#090E11] text-white border-0 hover:bg-[#1e293b]'"
                type="button"
                @click="goToLogin"
              >
                {{ $t('LoginRequired.login') }}
              </button>
              <button
                class="w-full h-12 rounded-full font-sans text-base font-bold cursor-pointer inline-flex items-center justify-center transition-all duration-200 active:scale-[0.98]"
                :class="isWasabi
                  ? 'bg-transparent text-[#DBE1F0] border border-white/10 hover:bg-white/5 hover:text-white'
                  : 'bg-transparent text-[#505C78] border border-[#E2E8F0] hover:bg-[#f8fafc] hover:text-[#0f172a]'"
                type="button"
                @click="closePopup"
              >
                {{ $t('LoginRequired.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, onUnmounted } from 'vue'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const restaurantStore = useRestaurantStore()
const isWasabi = computed(() => restaurantStore.slug === 'wasabi')
const localePath = useLocalePath()

const closePopup = () => {
  emit('close')
}

const goToLogin = () => {
  emit('close')
  navigateTo(localePath('/sign-in'))
}

let previousBodyOverflow = ''
let previousHtmlOverflow = ''

watch(() => props.isOpen, (newVal) => {
  if (process.client) {
    if (newVal) {
      previousBodyOverflow = document.body.style.overflow || ''
      previousHtmlOverflow = document.documentElement.style.overflow || ''
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
    }
  }
}, { immediate: true })

onUnmounted(() => {
  if (process.client) {
    document.body.style.overflow = previousBodyOverflow
    document.documentElement.style.overflow = previousHtmlOverflow
  }
})
</script>

<style scoped>
/* Transitions */
.login-required-modal-enter-from,
.login-required-modal-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

.login-required-modal-enter-active,
.login-required-modal-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.login-required-modal-enter-to,
.login-required-modal-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@media (max-width: 1023px) {
  .max-lg\:animate-slideUp {
    animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
}
</style>
